import { Modal, ModalFuncProps, Tooltip } from 'antd';
import TextArea, { TextAreaProps } from 'antd/lib/input/TextArea';
import classNames from 'classnames';
import { ReactNode, SyntheticEvent } from 'react';

import { TOOLTIP_PLACEMENT } from '@common/constants';
import cssColors from '@common/css/cssColors.module.scss';

import InstructionsBanner from '../instructions-banner/InstructionsBanner';
import InfoCircleFilled from '/public/assets/icons/info-circle-filled.svg';

import './ProductCard.scss';

interface ProductCardImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    showSelectedBanner?: boolean;
    showInfoIcon?: boolean;
    onInfoIconClick?: () => void;
}

const ProductCardImage = ({ showSelectedBanner, showInfoIcon, onInfoIconClick, ...props }: ProductCardImageProps) => {
    return (
        <div className="ProductCard__image-container">
            <img className="ProductCard__image" {...props} />
            {showSelectedBanner && <InstructionsBanner className="ProductCard__banner" />}
            {showInfoIcon ? (
                <InfoCircleFilled
                    className="ProductCard__info-icon"
                    onClick={(event: SyntheticEvent) => {
                        onInfoIconClick && onInfoIconClick();
                        event.stopPropagation();
                    }}
                />
            ) : null}
        </div>
    );
};

interface ProductCardModalProps extends ModalFuncProps {
    children?: ReactNode;
}

const ProductCardModal = ({ className, children, ...props }: ProductCardModalProps) => {
    return (
        <div onClick={(event) => event.stopPropagation()}>
            <Modal className={classNames('ProductCard__modal', className)} centered footer={null} closable {...props}>
                {children}
            </Modal>
        </div>
    );
};

const ProductCardTitle = ({ children }: { children?: string }) => {
    return (
        <Tooltip title={children} color={cssColors.toolTipColor} placement={TOOLTIP_PLACEMENT}>
            <span className="ProductCard__title">{children}</span>
        </Tooltip>
    );
};

const ProductCardSubtitle = ({ children }: { children?: string }) => {
    return <span className="ProductCard__subtitle">{children}</span>;
};

interface ProductCardTextAreaProps extends TextAreaProps {
    value?: string;
    placeholder?: string;
    required?: boolean;
    onValueChange?: (value: string) => void;
}

const ProductCardTextArea = ({ value, placeholder, required, onValueChange, ...props }: ProductCardTextAreaProps) => {
    return (
        <TextArea
            className={classNames('ProductCard__text-area', {
                'ProductCard__text-area--required': required && !value?.length,
            })}
            defaultValue={value}
            placeholder={placeholder}
            required={required}
            rows={2}
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => onValueChange && onValueChange(e.target.value)}
            {...props}
        />
    );
};

interface ProductCardProps {
    className?: string;
    isLarge?: boolean;
    isSelected?: boolean;
    onClick?: () => void;
    children?: ReactNode;
    additionalClasses?: classNames.Mapping;
}

const ProductCard = ({
    className,
    isLarge = false,
    isSelected = false,
    onClick,
    children,
    additionalClasses,
}: ProductCardProps) => {
    return (
        <div
            onClick={onClick}
            className={classNames(className, 'ProductCard', {
                'ProductCard--large': isLarge,
                'ProductCard--selected': isSelected,
                ...additionalClasses,
            })}
        >
            {children}
        </div>
    );
};

export default Object.assign(ProductCard, {
    Title: ProductCardTitle,
    Image: ProductCardImage,
    Modal: ProductCardModal,
    Subtitle: ProductCardSubtitle,
    TextArea: ProductCardTextArea,
});
